import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, X, Package, CheckCircle2, XCircle, ChevronRight, Camera, Users, Truck, Ruler, Upload, ImagePlus } from "lucide-react";

const STAGES = ["prototype", "fit_sample", "size_set", "pre_production", "top_of_production"];
const STAGE_LABELS = { prototype: "Prototype", fit_sample: "Fit Sample", size_set: "Size Set", pre_production: "Pre-Production", top_of_production: "TOP" };
const STATUS_STYLE = {
  pending: "bg-zinc-100 text-zinc-700",
  dispatched: "bg-blue-100 text-blue-800",
  received: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-red-100 text-red-700",
};

export default function SampleManagement() {
  const [samples, setSamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeStage, setActiveStage] = useState("all");
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [techpacks, setTechpacks] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [form, setForm] = useState({ techpack_id: "", stage: "prototype", supplier_id: "", quantity: 1, due_date: "", courier_reference: "", courier_name: "", tracking_url: "", estimated_delivery: "" });
  const [saving, setSaving] = useState(false);
  const [fitSessions, setFitSessions] = useState([]);
  const [dispatchForm, setDispatchForm] = useState({ courier_name: "", tracking_url: "", estimated_delivery: "" });
  const [showDispatchModal, setShowDispatchModal] = useState(false);
  const [measurementForm, setMeasurementForm] = useState([{ point: "", spec_value: "", actual_value: "", tolerance: "0.5" }]);
  const [activeFitSession, setActiveFitSession] = useState(null);
  const [measurementComparison, setMeasurementComparison] = useState(null);
  const [supplierMap, setSupplierMap] = useState({});
  const [approvalComment, setApprovalComment] = useState("");
  const [showApprovalInput, setShowApprovalInput] = useState(null);
  const [showPhotoInput, setShowPhotoInput] = useState(false);
  const [photoUrl, setPhotoUrl] = useState("");

  const load = () => {
    Promise.all([
      api.get("/sample-requests").then((r) => setSamples(r.data)).catch(() => {}),
      api.get("/techpacks").then((r) => setTechpacks(r.data)).catch(() => {}),
      api.get("/suppliers").then((r) => { setSuppliers(r.data); const m = {}; r.data.forEach((s) => { m[s.id] = s.company_name; }); setSupplierMap(m); }).catch(() => {}),
    ]).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/sample-requests", {
        techpack_id: form.techpack_id,
        stage: form.stage,
        supplier_id: form.supplier_id || undefined,
        quantity: parseInt(form.quantity, 10) || 1,
        due_date: form.due_date ? new Date(form.due_date).toISOString() : new Date(Date.now() + 14 * 86400000).toISOString(),
        courier_reference: form.courier_reference || "",
      });
      toast.success("Sample request created");
      setShowCreate(false);
      setForm({ techpack_id: "", stage: "prototype", supplier_id: "", quantity: 1, due_date: "", courier_reference: "", courier_name: "", tracking_url: "", estimated_delivery: "" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const openDispatchModal = (id) => {
    setDispatchForm({ courier_name: "", tracking_url: "", estimated_delivery: "" });
    setShowDispatchModal(id);
  };

  const dispatchSample = async (id) => {
    try {
      const payload = {
        dispatch_date: new Date().toISOString(),
        courier_name: dispatchForm.courier_name || undefined,
        tracking_url: dispatchForm.tracking_url || undefined,
        estimated_delivery: dispatchForm.estimated_delivery ? new Date(dispatchForm.estimated_delivery).toISOString() : undefined,
      };
      await api.patch(`/sample-requests/${id}`, payload);
      toast.success("Sample dispatched");
      setShowDispatchModal(false);
      setSelected(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const saveMeasurements = async (sessionId) => {
    const valid = measurementForm.filter((m) => m.point && m.spec_value && m.actual_value);
    if (!valid.length) {
      toast.error("Add at least one measurement");
      return;
    }
    try {
      await api.post(`/fit-sessions/${sessionId}/measurements`, {
        measurements: valid.map((m) => ({
          point: m.point,
          spec_value: parseFloat(m.spec_value),
          actual_value: parseFloat(m.actual_value),
          tolerance: parseFloat(m.tolerance) || 0.5,
        })),
      });
      toast.success("Measurements saved");
      setMeasurementForm([{ point: "", spec_value: "", actual_value: "", tolerance: "0.5" }]);
      loadComparison(sessionId);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const loadComparison = async (sessionId) => {
    try {
      const r = await api.get(`/fit-sessions/${sessionId}/measurement-comparison`);
      setMeasurementComparison(r.data);
    } catch {
      setMeasurementComparison(null);
    }
  };

  const receiveSample = async (id) => {
    try {
      await api.patch(`/sample-requests/${id}`, { received_date: new Date().toISOString(), received_quantity: selected?.quantity || 1 });
      toast.success("Sample received");
      setSelected(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const updateStatus = async (id, status, comment) => {
    try {
      const payload = { status };
      if (comment) payload.approval_comment = comment;
      await api.patch(`/sample-requests/${id}/status`, payload);
      toast.success(`Sample ${status}`);
      if (status === "approved" && selected) {
        const currentIdx = STAGES.indexOf(selected.stage);
        if (currentIdx >= 0 && currentIdx < STAGES.length - 1) {
          const nextStage = STAGES[currentIdx + 1];
          toast(`Sample approved! Consider creating a ${STAGE_LABELS[nextStage]} sample.`, { duration: 6000 });
        }
      }
      setShowApprovalInput(null);
      setApprovalComment("");
      setSelected(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const uploadPhoto = async (id) => {
    if (!photoUrl.trim()) {
      toast.error("Enter a photo URL");
      return;
    }
    try {
      await api.post(`/sample-requests/${id}/photos`, { url: photoUrl.trim() });
      toast.success("Photo added");
      setPhotoUrl("");
      setShowPhotoInput(false);
      load();
      const r = await api.get(`/sample-requests/${id}`);
      if (r.data) setSelected(r.data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const filtered = activeStage === "all" ? samples : samples.filter((s) => s.stage === activeStage);
  const stageCounts = STAGES.reduce((acc, s) => { acc[s] = samples.filter((sm) => sm.stage === s).length; return acc; }, {});

  return (
    <div className="p-10 max-w-6xl mx-auto">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-zinc-400">PLM</p>
          <h1 className="font-heading text-3xl font-bold mt-2 tracking-tight">Sample Management</h1>
          <p className="text-zinc-500 mt-2 text-sm">Track sample requests through the development pipeline.</p>
        </div>
        <button onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 bg-zinc-950 text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors">
          <Plus size={15} /> New sample request
        </button>
      </div>

      {/* Pipeline View */}
      <div className="mt-8 flex gap-2 overflow-x-auto pb-2">
        <button onClick={() => setActiveStage("all")}
          className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap ${activeStage === "all" ? "bg-zinc-950 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}>
          All ({samples.length})
        </button>
        {STAGES.map((s) => (
          <button key={s} onClick={() => setActiveStage(s)}
            className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap ${activeStage === s ? "bg-zinc-950 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}>
            {STAGE_LABELS[s]} ({stageCounts[s]})
          </button>
        ))}
      </div>

      {/* Pipeline Progress Bar */}
      <div className="mt-4 flex gap-1 h-2">
        {STAGES.map((s) => (
          <div key={s} className="flex-1 bg-zinc-100 rounded-full overflow-hidden" title={STAGE_LABELS[s]}>
            <div className="h-full bg-zinc-800 rounded-full" style={{ width: `${samples.length ? (stageCounts[s] / samples.length) * 100 : 0}%` }} />
          </div>
        ))}
      </div>
      <div className="flex gap-1 mt-1">
        {STAGES.map((s) => <span key={s} className="flex-1 text-[9px] text-zinc-400 text-center">{STAGE_LABELS[s]}</span>)}
      </div>

      {loading ? (
        <div className="mt-10 text-sm text-zinc-400">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="mt-10 border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-400">
          <Package size={26} className="mx-auto mb-3" />
          <p className="text-sm">No samples for this stage.</p>
        </div>
      ) : (
        <div className="mt-6 flex gap-6">
          <div className="flex-1 border border-zinc-200 bg-white rounded-lg overflow-hidden">
            <div className="grid grid-cols-12 px-6 py-3 border-b border-zinc-100 text-[11px] uppercase tracking-[0.15em] text-zinc-400">
              <span className="col-span-3">Style</span>
              <span className="col-span-2">Stage</span>
              <span className="col-span-2">Status</span>
              <span className="col-span-3">Supplier</span>
              <span className="col-span-2 text-right">Details</span>
            </div>
            {filtered.map((s) => (
              <div key={s.id} className={`grid grid-cols-12 items-center px-6 py-4 border-b border-zinc-50 cursor-pointer hover:bg-zinc-50 ${selected?.id === s.id ? "bg-zinc-50" : ""}`}
                onClick={() => {
                  setSelected(s);
                  api.get(`/sample-requests/${s.id}/fit-sessions`).then((r) => setFitSessions(r.data)).catch(() => setFitSessions([]));
                }}>
                <span className="col-span-3 text-sm font-medium">{s.supplier_id && supplierMap[s.supplier_id] ? supplierMap[s.supplier_id] : s.techpack_id?.slice(0, 8) || "N/A"}</span>
                <span className="col-span-2 text-xs text-zinc-600">{STAGE_LABELS[s.stage] || s.stage}</span>
                <span className="col-span-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_STYLE[s.status] || STATUS_STYLE.pending}`}>
                    {s.status?.replace("_", " ")}
                  </span>
                </span>
                <span className="col-span-3 text-sm text-zinc-600 truncate">Qty: {s.quantity} | Due: {s.due_date?.slice(0, 10) || "N/A"}</span>
                <span className="col-span-2 text-right"><ChevronRight size={14} className="inline text-zinc-400" /></span>
              </div>
            ))}
          </div>

          {selected && (
            <div className="w-72 shrink-0 border border-zinc-200 bg-white rounded-lg p-6">
              <h3 className="font-heading font-semibold">Sample {selected.id?.slice(0, 8)}</h3>
              <div className="mt-3 space-y-3">
                {selected.supplier_id && supplierMap[selected.supplier_id] && (
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Supplier</p>
                    <p className="text-sm font-medium">{supplierMap[selected.supplier_id]}</p>
                  </div>
                )}
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Stage</p>
                  <p className="text-sm">{STAGE_LABELS[selected.stage] || selected.stage}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Quantity</p>
                  <p className="text-sm">{selected.quantity}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Due Date</p>
                  <p className="text-sm text-zinc-600">{selected.due_date?.slice(0, 10) || "N/A"}</p>
                </div>
                {(selected.courier_reference || selected.courier_name || selected.tracking_url) && (
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 flex items-center gap-1"><Truck size={10} /> Courier Info</p>
                    {selected.courier_name && <p className="text-sm text-zinc-600">{selected.courier_name}</p>}
                    {selected.courier_reference && <p className="text-xs text-zinc-500">Ref: {selected.courier_reference}</p>}
                    {selected.tracking_url && (
                      <a href={selected.tracking_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline break-all">
                        Track shipment
                      </a>
                    )}
                    {selected.estimated_delivery && (
                      <p className="text-xs text-zinc-500 mt-0.5">ETA: {selected.estimated_delivery?.slice(0, 10)}</p>
                    )}
                  </div>
                )}
                {selected.dispatch_date && (
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Dispatched</p>
                    <p className="text-sm text-zinc-600">{selected.dispatch_date?.slice(0, 10)}</p>
                  </div>
                )}
                {selected.received_date && (
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Received</p>
                    <p className="text-sm text-zinc-600">{selected.received_date?.slice(0, 10)} (Qty: {selected.received_quantity})</p>
                  </div>
                )}
                {selected.approval_comment && (
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Approval Comment</p>
                    <p className="text-sm text-zinc-600">{selected.approval_comment}</p>
                  </div>
                )}
                {selected.approver_id && (
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Approved By</p>
                    <p className="text-sm text-zinc-600">{selected.approver_name || selected.approver_id?.slice(0, 8)}</p>
                  </div>
                )}
                {selected.created_at && (
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Requested</p>
                    <p className="text-sm text-zinc-600">{selected.created_at?.slice(0, 10)}</p>
                  </div>
                )}
                {/* Sample Photos */}
                {(selected.photos || []).length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 flex items-center gap-1"><Camera size={10} /> Photos</p>
                    <div className="mt-1 flex gap-1.5 overflow-x-auto">
                      {selected.photos.map((p, i) => (
                        <img key={i} src={p.url || p} alt={`Sample ${i + 1}`} className="w-14 h-14 rounded border border-zinc-200 object-cover shrink-0" />
                      ))}
                    </div>
                  </div>
                )}
                {/* Photo Upload */}
                <div>
                  {showPhotoInput ? (
                    <div className="space-y-1.5">
                      <input value={photoUrl} onChange={(e) => setPhotoUrl(e.target.value)}
                        placeholder="Photo URL"
                        className="w-full border border-zinc-300 rounded-md px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-zinc-950" />
                      <div className="flex gap-1.5">
                        <button onClick={() => uploadPhoto(selected.id)}
                          className="flex-1 inline-flex items-center justify-center gap-1 bg-zinc-950 text-white px-2 py-1.5 rounded-md text-xs font-medium hover:bg-zinc-800">
                          <Upload size={11} /> Upload
                        </button>
                        <button onClick={() => { setShowPhotoInput(false); setPhotoUrl(""); }}
                          className="px-2 py-1.5 text-xs text-zinc-500 hover:text-zinc-800">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <button onClick={() => setShowPhotoInput(true)}
                      className="w-full inline-flex items-center justify-center gap-1.5 bg-zinc-100 text-zinc-700 px-3 py-1.5 rounded-md text-xs font-medium hover:bg-zinc-200">
                      <ImagePlus size={12} /> Upload Photo
                    </button>
                  )}
                </div>
              </div>
              {/* Fit Sessions */}
              {fitSessions.length > 0 && (
                <div className="mt-4 pt-3 border-t border-zinc-100">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 flex items-center gap-1 mb-2"><Users size={10} /> Fit Sessions</p>
                  <div className="space-y-2">
                    {fitSessions.map((fs) => (
                      <div key={fs.id} className="bg-zinc-50 rounded-md p-2.5">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium">{fs.session_date ? new Date(fs.session_date).toLocaleDateString() : "Session"}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${fs.outcome === "pass" ? "bg-emerald-100 text-emerald-800" : fs.outcome === "fail" ? "bg-red-100 text-red-700" : "bg-zinc-100 text-zinc-600"}`}>
                            {fs.outcome || "pending"}
                          </span>
                        </div>
                        {fs.notes && <p className="text-[10px] text-zinc-500 mt-1">{fs.notes}</p>}
                        {(fs.attendees || []).length > 0 && (
                          <p className="text-[10px] text-zinc-400 mt-1">Attendees: {fs.attendees.join(", ")}</p>
                        )}
                        {(fs.photos || []).length > 0 && (
                          <div className="mt-1.5 flex gap-1 overflow-x-auto">
                            {fs.photos.map((p, i) => (
                              <img key={i} src={p.url || p} alt={`Fit ${i + 1}`} className="w-10 h-10 rounded border border-zinc-200 object-cover shrink-0" />
                            ))}
                          </div>
                        )}
                        {(fs.measurements || []).length > 0 && (
                          <p className="text-[10px] text-zinc-400 mt-1">{fs.measurements.length} measurement(s)</p>
                        )}
                        <button onClick={() => { setActiveFitSession(fs.id); loadComparison(fs.id); }}
                          className="mt-1.5 text-[10px] text-blue-600 hover:underline flex items-center gap-0.5">
                          <Ruler size={9} /> Measurements
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="mt-5 pt-4 border-t border-zinc-100 space-y-2">
                {selected.status === "pending" && (
                  <button onClick={() => openDispatchModal(selected.id)}
                    className="w-full inline-flex items-center justify-center gap-1.5 bg-blue-50 text-blue-800 px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-100">
                    <Package size={14} /> Mark Dispatched
                  </button>
                )}
                {selected.status === "dispatched" && (
                  <button onClick={() => receiveSample(selected.id)}
                    className="w-full inline-flex items-center justify-center gap-1.5 bg-amber-50 text-amber-800 px-4 py-2 rounded-md text-sm font-medium hover:bg-amber-100">
                    <CheckCircle2 size={14} /> Mark Received
                  </button>
                )}
                {selected.status === "received" && !showApprovalInput && (
                  <>
                    <button onClick={() => setShowApprovalInput("approved")}
                      className="w-full inline-flex items-center justify-center gap-1.5 bg-emerald-50 text-emerald-800 px-4 py-2 rounded-md text-sm font-medium hover:bg-emerald-100">
                      <CheckCircle2 size={14} /> Approve
                    </button>
                    <button onClick={() => setShowApprovalInput("rejected")}
                      className="w-full inline-flex items-center justify-center gap-1.5 bg-red-50 text-red-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-red-100">
                      <XCircle size={14} /> Reject
                    </button>
                  </>
                )}
                {showApprovalInput && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-zinc-700">
                      {showApprovalInput === "approved" ? "Approval" : "Rejection"} comment
                    </p>
                    <textarea value={approvalComment} onChange={(e) => setApprovalComment(e.target.value)}
                      placeholder="Add a comment (optional)..."
                      rows={2}
                      className="w-full border border-zinc-300 rounded-md px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-zinc-950 resize-none" />
                    <div className="flex gap-1.5">
                      <button onClick={() => updateStatus(selected.id, showApprovalInput, approvalComment)}
                        className={`flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium ${showApprovalInput === "approved" ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-red-600 text-white hover:bg-red-700"}`}>
                        {showApprovalInput === "approved" ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
                        Confirm {showApprovalInput === "approved" ? "Approval" : "Rejection"}
                      </button>
                      <button onClick={() => { setShowApprovalInput(null); setApprovalComment(""); }}
                        className="px-2 py-1.5 text-xs text-zinc-500 hover:text-zinc-800">Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-lg p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-lg">New sample request</h2>
              <button onClick={() => setShowCreate(false)} className="p-1.5 hover:bg-zinc-100 rounded-md"><X size={16} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">Techpack</label>
                <select value={form.techpack_id} onChange={(e) => setForm({ ...form, techpack_id: e.target.value })}
                  className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
                  <option value="">Select techpack...</option>
                  {techpacks.map((t) => <option key={t.id} value={t.id}>{t.style_name || t.style_number || t.id.slice(0, 8)}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">Sample stage</label>
                <select value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })}
                  className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
                  {STAGES.map((s) => <option key={s} value={s}>{STAGE_LABELS[s]}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">Supplier (optional)</label>
                <select value={form.supplier_id} onChange={(e) => setForm({ ...form, supplier_id: e.target.value })}
                  className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
                  <option value="">Select supplier...</option>
                  {suppliers.map((s) => <option key={s.id} value={s.id}>{s.company_name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-zinc-500 mb-1 block">Quantity</label>
                  <input type="number" min="1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                    className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
                </div>
                <div>
                  <label className="text-xs text-zinc-500 mb-1 block">Due date</label>
                  <input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                    className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
                </div>
              </div>
              <input value={form.courier_reference} onChange={(e) => setForm({ ...form, courier_reference: e.target.value })} placeholder="Courier reference (optional)"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <input value={form.courier_name} onChange={(e) => setForm({ ...form, courier_name: e.target.value })} placeholder="Courier name (optional)"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <input value={form.tracking_url} onChange={(e) => setForm({ ...form, tracking_url: e.target.value })} placeholder="Tracking URL (optional)"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">Estimated delivery (optional)</label>
                <input type="date" value={form.estimated_delivery} onChange={(e) => setForm({ ...form, estimated_delivery: e.target.value })}
                  className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              </div>
              <button onClick={create} disabled={!form.techpack_id || !form.due_date || saving}
                className="w-full bg-zinc-950 text-white py-3 rounded-md font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
                {saving ? "Creating..." : "Submit request"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dispatch Modal */}
      {showDispatchModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setShowDispatchModal(false)}>
          <div className="bg-white rounded-lg p-8 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-lg flex items-center gap-2"><Truck size={18} /> Dispatch details</h2>
              <button onClick={() => setShowDispatchModal(false)} className="p-1.5 hover:bg-zinc-100 rounded-md"><X size={16} /></button>
            </div>
            <div className="space-y-3">
              <input value={dispatchForm.courier_name} onChange={(e) => setDispatchForm({ ...dispatchForm, courier_name: e.target.value })}
                placeholder="Courier name" className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <input value={dispatchForm.tracking_url} onChange={(e) => setDispatchForm({ ...dispatchForm, tracking_url: e.target.value })}
                placeholder="Tracking URL" className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">Estimated delivery</label>
                <input type="date" value={dispatchForm.estimated_delivery} onChange={(e) => setDispatchForm({ ...dispatchForm, estimated_delivery: e.target.value })}
                  className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              </div>
              <button onClick={() => dispatchSample(showDispatchModal)}
                className="w-full bg-blue-600 text-white py-2.5 rounded-md font-medium hover:bg-blue-700 transition-colors">
                Confirm dispatch
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Measurement Entry/Comparison Modal */}
      {activeFitSession && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => { setActiveFitSession(null); setMeasurementComparison(null); }}>
          <div className="bg-white rounded-lg p-8 w-full max-w-lg max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-lg flex items-center gap-2"><Ruler size={18} /> Measurements</h2>
              <button onClick={() => { setActiveFitSession(null); setMeasurementComparison(null); }} className="p-1.5 hover:bg-zinc-100 rounded-md"><X size={16} /></button>
            </div>

            {/* Comparison table */}
            {measurementComparison && measurementComparison.measurements.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-zinc-700">Comparison</p>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${measurementComparison.summary.pass_rate === 100 ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                    {measurementComparison.summary.passed}/{measurementComparison.summary.total} pass ({measurementComparison.summary.pass_rate}%)
                  </span>
                </div>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 border-b border-zinc-100">
                      <th className="text-left py-1.5">Point</th>
                      <th className="text-right py-1.5">Spec</th>
                      <th className="text-right py-1.5">Actual</th>
                      <th className="text-right py-1.5">Tol</th>
                      <th className="text-center py-1.5">Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {measurementComparison.measurements.map((m, i) => (
                      <tr key={i} className="border-b border-zinc-50">
                        <td className="py-1.5">{m.point}</td>
                        <td className="text-right">{m.spec_value}</td>
                        <td className="text-right">{m.actual_value}</td>
                        <td className="text-right">{m.tolerance}</td>
                        <td className="text-center">
                          {m.pass ? <CheckCircle2 size={12} className="inline text-emerald-600" /> : <XCircle size={12} className="inline text-red-500" />}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Entry form */}
            <p className="text-xs font-medium text-zinc-700 mb-2">Add measurements</p>
            <div className="space-y-2">
              {measurementForm.map((m, i) => (
                <div key={i} className="grid grid-cols-12 gap-1.5 items-end">
                  <input className="col-span-4 border border-zinc-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-zinc-950"
                    placeholder="Point" value={m.point} onChange={(e) => { const u = [...measurementForm]; u[i] = { ...u[i], point: e.target.value }; setMeasurementForm(u); }} />
                  <input className="col-span-2 border border-zinc-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-zinc-950"
                    placeholder="Spec" type="number" step="0.1" value={m.spec_value} onChange={(e) => { const u = [...measurementForm]; u[i] = { ...u[i], spec_value: e.target.value }; setMeasurementForm(u); }} />
                  <input className="col-span-2 border border-zinc-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-zinc-950"
                    placeholder="Actual" type="number" step="0.1" value={m.actual_value} onChange={(e) => { const u = [...measurementForm]; u[i] = { ...u[i], actual_value: e.target.value }; setMeasurementForm(u); }} />
                  <input className="col-span-2 border border-zinc-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-zinc-950"
                    placeholder="Tol" type="number" step="0.1" value={m.tolerance} onChange={(e) => { const u = [...measurementForm]; u[i] = { ...u[i], tolerance: e.target.value }; setMeasurementForm(u); }} />
                  <button className="col-span-2 text-xs text-red-500 hover:text-red-700" onClick={() => { if (measurementForm.length > 1) setMeasurementForm(measurementForm.filter((_, j) => j !== i)); }}>
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-3">
              <button onClick={() => setMeasurementForm([...measurementForm, { point: "", spec_value: "", actual_value: "", tolerance: "0.5" }])}
                className="text-xs text-blue-600 hover:underline flex items-center gap-1"><Plus size={11} /> Add row</button>
            </div>
            <button onClick={() => saveMeasurements(activeFitSession)}
              className="w-full mt-4 bg-zinc-950 text-white py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors">
              Save measurements
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
